import { DealerManagementService } from '../../../../services/dealer-management.service';
import { getPasswordFieldDesc } from 'src/utils/helpers';
import { HttpStatusCode } from '../../../../utils/enums/http-method.enum';
import Input from '../../../../components/forms/input';
import { isValidPasswordReset } from 'src/utils/enums/passwordReset.enum';
import { LoginService } from '../../../../services/login.service';
import MfaCodesInputs from 'src/views/auth/components/mfaCodesInputs';
import Spinner from '../../../../components/ui/spinner';
import { useDispatch } from 'react-redux';
import { useFormik } from 'formik';
import { useLocalization } from '../../../../utils/useLocalization';
import { USER_ACTIONS } from '../../../../utils/enums/user-store-actions.enum';
import { useTranslation } from 'react-i18next';
import {
    ACCESS_USER_NAME,
    ASSOCIATIONS_NAME,
    COGNITO_TOKEN_NAME,
    DEALER_COUNTRY_NAME,
    DEFAULTDEALER_NAME,
    DISTRICTS_NAME,
    FULL_NAME,
    PARTYID_NAME,
    PERMISSIONS_NAME,
    PERMISSIONS_TOKEN_NAME,
    USER_NAME,
} from '../../../../config';
import {
    FORGOT_PASSWORD_MFA_DESCRIPTION,
    FORGOT_PASSWORD_MFA_ENTER,
    FORGOT_PASSWORD_MFA_TITLE,
    LABEL_FIELD_ERROR_INVALID_OR_EXPIRED_CODE,
    LABEL_PASSWORD_ERROR_ATTEMPT_SUBTITLE,
    LABEL_TEXT_ERROR_ATTEMPT_TITLE,
} from '../../../../localization/resouceKeys';
import {
    forgotPasswordValidations,
    passwordResetValidations,
} from './validations';
import React, {
    Dispatch,
    SetStateAction,
    useEffect,
    useRef,
    useState,
} from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import './styles.scss';

export interface IProps {
    setShowSuccess: Dispatch<SetStateAction<boolean>>;
    setShowLocked: Dispatch<SetStateAction<boolean>>;
}

interface IMfaInputProps {
    mfaTokenRef: React.MutableRefObject<string>;
    attempts: number;
    submitHandler: () => Promise<void>;
    mfaCodeError: boolean;
}

const INITIAL_VALUE = 0;

const MfaInput: React.FC<IMfaInputProps> = ({
    mfaTokenRef,
    attempts,
    submitHandler,
    mfaCodeError,
}) => {
    const MAX_ATTEMPTS = 5;
    const { t } = useTranslation();
    const [loading, setLoading] = useState<boolean>(false);
    const isMountedRef = useRef<boolean>(true);

    useEffect(() => {
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const {
        values,
        errors,
        touched,
        handleChange,
        handleSubmit,
        handleBlur,
        setFieldValue,
        setFieldTouched,
    } = useFormik({
        initialValues: {
            code1: '',
            code2: '',
            code3: '',
            code4: '',
            code5: '',
            code6: '',
        },
        validationSchema: forgotPasswordValidations,
        validateOnChange: true,
        validateOnBlur: true,
        onSubmit: handlerSubmit,
    });

    async function handlerSubmit() {
        setLoading(true);
        const code = Object.values(values).join('');
        mfaTokenRef.current = code;
        try {
            await submitHandler();
        } finally {
            if (isMountedRef.current) {
                setLoading(false);
            }
        }
    }

    function shouldShowAttempt() {
        return attempts > INITIAL_VALUE;
    }

    return (
        <div className='mfainput-container u-mt-xlarge'>
            {shouldShowAttempt() && (
                <div className={'attempt-section u-mb'}>
                    <span className={'attempt-title'}>
                        {t(LABEL_TEXT_ERROR_ATTEMPT_TITLE, {
                            numberAttempts: MAX_ATTEMPTS - attempts,
                        })}
                    </span>
                    <p>
                        {t(LABEL_PASSWORD_ERROR_ATTEMPT_SUBTITLE, {
                            maxAttempts: MAX_ATTEMPTS,
                        })}
                    </p>
                </div>
            )}
            <form
                onSubmit={handleSubmit}
                autoComplete={'off'}
                className={'u-mt-xxsmall u-mb-small'}
            >
                <div className={'mfa-form-container'}>
                    <div className={'header u-mb-xxsmall'}>
                        <h4 className='u-mb-large'>
                            {t(FORGOT_PASSWORD_MFA_TITLE)}
                        </h4>
                        <h5 className='u-mb-large'>
                            {t(FORGOT_PASSWORD_MFA_ENTER)}
                        </h5>
                        <p className='u-mb-large'>
                            {t(FORGOT_PASSWORD_MFA_DESCRIPTION)}
                        </p>
                    </div>
                    <MfaCodesInputs
                        handleBlur={handleBlur}
                        handleChange={handleChange}
                        values={values}
                        touched={touched}
                        setFieldValue={setFieldValue}
                        setFieldTouched={setFieldTouched}
                    />
                    {mfaCodeError && (
                        <span
                            className={'c-error-message'}
                            style={{ position: 'absolute' }}
                        >
                            {t(LABEL_FIELD_ERROR_INVALID_OR_EXPIRED_CODE)}
                        </span>
                    )}
                </div>
                <div className='o-button-container o-button-container--stretch u-mt-large'>
                    <button
                        className='c-btn o-button-container__button'
                        disabled={
                            values.code1 === '' ||
                            values.code2 === '' ||
                            values.code3 === '' ||
                            values.code4 === '' ||
                            values.code5 === '' ||
                            values.code6 === '' ||
                            !!Object.keys(errors).length ||
                            loading
                        }
                        type={'submit'}
                    >
                        <span className='c-btn__text'>
                            {loading ? <Spinner /> : 'Submit'}
                        </span>
                    </button>
                    <span></span>
                </div>
            </form>
        </div>
    );
};

const PasswordChange: React.FC<IProps> = ({
    setShowSuccess,
    setShowLocked,
}) => {
    const { i18n } = useTranslation();
    const { accountRegistration, passwordReset, addNewUser } =
        useLocalization();
    const [attempts, setAttempts] = useState<number>(INITIAL_VALUE);
    const [searchParams] = useSearchParams();
    const navigation = useNavigate();
    const [code, setCode] = useState('');
    const userDispatch = useDispatch();
    useEffect(() => {
        if (
            !isTokenInSearchParams() ||
            (isForgotPassInSearchParams() &&
                !isValidPasswordReset([...searchParams][1][0]))
        ) {
            showNotification('Network');
        } else {
            setCode([...searchParams][0][0]);
        }
    }, [searchParams]);
    const [loading, setLoading] = useState<boolean>(false);
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [showPasswordConfirm, setShowPasswordConfirm] =
        useState<boolean>(false);
    const [networkError, setNetworkError] = useState<boolean>(false);
    const [tokenError, setTokenError] = useState<boolean>(false);
    const [mfaCodeError, setMfaCodeError] = useState<boolean>(false);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [submitErrors, setSubmitErrors] = useState({
        password: '',
        passwordConfirm: '',
    });
    const [showMfa, setShowMfa] = useState<boolean>(false);
    const mfaTokenRef = useRef<string>('');
    const {
        values,
        errors,
        touched,
        handleChange,
        handleSubmit,
        handleBlur,
        setFieldTouched,
    } = useFormik({
        initialValues: {
            password: '',
            passwordConfirm: '',
        },
        validationSchema: passwordResetValidations,
        onSubmit: handlerSubmit,
    });

    const passwordDesc = `${passwordReset.LABEL_RULES_PASSWORD}<br>
        ${getPasswordFieldDesc(values.password, accountRegistration)}`;

    function onCancelHandler() {
        navigation('/login');
    }

    function isTokenInSearchParams() {
        return searchParams && [...searchParams][0] && [...searchParams][0][0];
    }

    function isForgotPassInSearchParams() {
        return searchParams && [...searchParams][1] && [...searchParams][1][0];
    }

    function showNotification(type: string) {
        cleanUpNotifications();

        if (type === 'Token') {
            setShowMfa(false);
            setTokenError(true);
        } else if (type === 'MFA') {
            setMfaCodeError(true);
        } else {
            setShowMfa(false);
            setNetworkError(true);
        }
    }

    function cleanUpNotifications() {
        setTokenError(false);
        setNetworkError(false);
        setMfaCodeError(false);
    }

    async function handleAuthentication(
        password: string,
        mfaCode: string,
        username?: string,
    ) {
        try {
            if (!username) {
                console.error('Username not available for authentication');
                showNotification('Network');
                return;
            }

            const loginService = new LoginService();
            const resp = await loginService.sendAuthCode(
                username,
                mfaCode,
                password,
            );

            if (resp.data.statusCode == HttpStatusCode.LOCKED) {
                setShowMfa(false);
                setShowLocked(true);
                userDispatch({
                    type: USER_ACTIONS.SET_BLOCKED,
                    payload: true,
                });
                return;
            }

            sessionStorage.setItem(COGNITO_TOKEN_NAME, resp.data.cognitoToken);
            sessionStorage.setItem(USER_NAME, username);

            const request_profile = await loginService.getDealerProfile(
                resp.data.permissionsToken,
            );

            if (
                request_profile &&
                request_profile.status === HttpStatusCode.SUCCESS
            ) {
                const {
                    dsourceProfile,
                    userProfile,
                    userPermissions,
                    associations,
                    districts,
                } = request_profile.data.userProfile.userData;
                const { countryCode } =
                    dsourceProfile?.user?.phoneNumbers?.[0] ?? {};
                const fullName = `${userProfile.userFirstName} ${userProfile.userLastName}`;
                sessionStorage.setItem(
                    DEALER_COUNTRY_NAME,
                    countryCode || 'NA',
                );
                sessionStorage.setItem(
                    PERMISSIONS_TOKEN_NAME,
                    resp.data.permissionsToken,
                );
                sessionStorage.setItem(FULL_NAME, fullName);
                sessionStorage.setItem(
                    DEFAULTDEALER_NAME,
                    userProfile.userDefaultDealer,
                );
                sessionStorage.setItem(
                    PERMISSIONS_NAME,
                    JSON.stringify(userPermissions),
                );
                sessionStorage.setItem(
                    ASSOCIATIONS_NAME,
                    JSON.stringify(associations),
                );
                sessionStorage.setItem(
                    ACCESS_USER_NAME,
                    JSON.stringify(userProfile.userIsFieldAccessUser),
                );
                sessionStorage.setItem(
                    PARTYID_NAME,
                    JSON.stringify(userProfile.userPartyId),
                );

                if (userProfile.userIsFieldAccessUser === true) {
                    sessionStorage.setItem(
                        DISTRICTS_NAME,
                        JSON.stringify(districts),
                    );
                }

                userDispatch({
                    type: USER_ACTIONS.MFA_TOKEN_VALID,
                    payload: {
                        fullName,
                        cognitoToken: resp.data.permissionsToken,
                        permissionsToken: resp.data.permissionsToken,
                        dealerCountry: countryCode || 'NA',
                        accessUser: userProfile.userIsFieldAccessUser,
                        districs: districts || [],
                        partyId: userProfile.userPartyId,
                    },
                });
                navigation('/login');
            } else {
                throw Error('Profile fetch failed');
            }
        } catch {
            showNotification('Network');
        }
    }

    async function handleStatusCode(
        statusCode: number,
        loginAttempCount?: number,
        username?: string,
    ) {
        switch (statusCode) {
            case HttpStatusCode.SUCCESS:
                if (!isForgotPassInSearchParams()) {
                    setShowSuccess(true);
                    break;
                }
                await handleAuthentication(
                    values.password,
                    mfaTokenRef.current,
                    username,
                );
                break;
            case HttpStatusCode.FORBIDDEN:
                setShowMfa(false);
                showNotification('Token');
                break;
            case HttpStatusCode.UNAUTHORIZED:
                if (loginAttempCount !== undefined) {
                    setAttempts(loginAttempCount);
                }
                showNotification('MFA');
                break;
            case HttpStatusCode.LOCKED:
                setShowMfa(false);
                setShowLocked(true);
                break;
            default:
                setShowMfa(false);
                showNotification('Network');
        }
    }

    async function handlerSubmit() {
        cleanUpNotifications();

        if (!code) {
            showNotification('Network');
            return;
        }

        if (isForgotPassInSearchParams() && !showMfa) {
            setShowMfa(true);
            return;
        }

        try {
            setLoading(true);
            const { password } = values;
            const service = new DealerManagementService();
            const res = await service.sendUpdatePasswordRequest({
                newPassword: password,
                token: code,
                mfaToken: isForgotPassInSearchParams()
                    ? mfaTokenRef.current
                    : undefined,
            });

            if (res?.data) {
                await handleStatusCode(
                    res.data.statusCode,
                    res.data.loginAttempCount,
                    res.data.username,
                );
            }
        } catch {
            showNotification('Network');
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        const handleLanguageChange = () => {
            Object.keys(touched).forEach((field) => {
                setFieldTouched(field, true, true);
            });
        };

        i18n.on('languageChanged', handleLanguageChange);
        return () => {
            i18n.off('languageChanged', handleLanguageChange);
        };
    }, [i18n, touched, errors, setFieldTouched]);

    return (
        <div className={'password_reset_container'}>
            {showMfa ? (
                <MfaInput
                    mfaTokenRef={mfaTokenRef}
                    attempts={attempts}
                    submitHandler={handlerSubmit}
                    mfaCodeError={mfaCodeError}
                />
            ) : (
                <>
                    <div className={'header u-mb-xxsmall u-mt-xlarge'}>
                        {(networkError || tokenError) && (
                            <section
                                id='js-notifications-group'
                                className='c-notifications-group  js-panelgroup js-panelgroup js-is-fx is-stacked js-is-ready u-mb-small u-mt-xsmall'
                            >
                                <div
                                    className={
                                        'c-notification__container  js-notification-container'
                                    }
                                >
                                    <div className='c-notification  c-notification--error'>
                                        <div className='c-notification__outer-wrap'>
                                            <div className='c-notification__inner-wrap'>
                                                <div className='c-notification__content'>
                                                    {networkError ? (
                                                        <p className='c-notification__text'>
                                                            {
                                                                accountRegistration.ACC_REG_LABEL_NOTIFICATION_ERROR
                                                            }
                                                        </p>
                                                    ) : null}
                                                    {tokenError ? (
                                                        <p className='c-notification__text'>
                                                            {
                                                                passwordReset.TOKEN_ERROR
                                                            }
                                                            <span
                                                                className='notification_link'
                                                                onClick={
                                                                    onCancelHandler
                                                                }
                                                            >
                                                                {
                                                                    passwordReset.TOKEN_LINK
                                                                }
                                                            </span>
                                                            {
                                                                passwordReset.TOKEN_ERROR2
                                                            }
                                                        </p>
                                                    ) : null}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </section>
                        )}
                        <h4>{passwordReset.TITLE}</h4>
                        <p className={'description u-mt'}>
                            {passwordReset.DESCRIPTION}
                        </p>
                        <p className={'description-fields u-mt-xsmall'}>
                            <span>*</span>
                            {accountRegistration.ACC_REG_LABEL_FIELD_REQUIRED}
                        </p>
                    </div>
                    <form
                        onSubmit={handleSubmit}
                        autoComplete={'off'}
                        className={'resetForm u-mt-large'}
                    >
                        <Input
                            labelName={passwordReset.LABEL_PASSWORD}
                            type={showPassword ? 'text' : 'password'}
                            name={'password'}
                            icon={'eye'}
                            handlerChange={handleChange}
                            handlerBlur={handleBlur}
                            handlerIcon={() => {
                                setShowPassword(!showPassword);
                            }}
                            value={values.password}
                            asErrors={errors.password}
                            asTouched={touched.password}
                            asSubmitError={submitErrors.password}
                            disabled={!code}
                            autoComplete='off'
                            description={passwordDesc}
                        />
                        <Input
                            labelName={
                                accountRegistration.ACC_REG_LABEL_PASSWORD_CONFIRM
                            }
                            type={showPasswordConfirm ? 'text' : 'password'}
                            name={'passwordConfirm'}
                            icon={'eye'}
                            handlerChange={handleChange}
                            handlerBlur={handleBlur}
                            handlerIcon={() => {
                                setShowPasswordConfirm(!showPasswordConfirm);
                            }}
                            value={values.passwordConfirm}
                            asErrors={errors.passwordConfirm}
                            asTouched={touched.passwordConfirm}
                            asSubmitError={submitErrors.passwordConfirm}
                            disabled={!code}
                            autoComplete='off'
                        />
                        <div className='o-button-container'>
                            <button
                                className={
                                    'c-btn c-btn--secondary o-button-container__button'
                                }
                                type={'button'}
                                onClick={onCancelHandler}
                            >
                                <span className='c-btn__text'>
                                    {addNewUser.MODAL_LABEL_BUTTON_CANCEL}
                                </span>
                            </button>
                            <button
                                className='c-btn o-button-container__button'
                                disabled={
                                    code === '' ||
                                    values.password === '' ||
                                    values.passwordConfirm === '' ||
                                    values.password !==
                                        values.passwordConfirm ||
                                    !!Object.keys(errors).length ||
                                    loading
                                }
                                type={'submit'}
                            >
                                <span className='c-btn__text'>
                                    {loading ? (
                                        <Spinner />
                                    ) : (
                                        accountRegistration.ACC_REG_LABEL_CONTINUE
                                    )}
                                </span>
                            </button>
                        </div>
                    </form>{' '}
                </>
            )}
        </div>
    );
};

export default PasswordChange;
